import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Bell,
  BarChart3,
  BookOpen,
  Bug,
  Calendar,
  ChevronDown,
  Code2,
  Database,
  FileText,
  GitBranch,
  GitCompareArrows,
  Grid2X2,
  HelpCircle,
  Home,
  Layers,
  ListChecks,
  Lightbulb,
  Megaphone,
  MessageSquare,
  MessageSquareCode,
  Network,
  Shield,
  ShieldCheck,
  UserCog,
  Users,
} from "lucide-react";
import { WeaveIcon } from "@/components/atoms/WeaveIcon";
import {
  FeedbackModal,
  RequestFeatureModal,
} from "@/components/molecules/feedback";
import { ThemeToggle } from "@/components/molecules/ThemeToggle";
import { UserProfile } from "@/components/molecules/UserProfile";
import { ActivityDropdown } from "@/components/molecules/ActivityDropdown";
import { IngestOngoingJobSync } from "@/components/organisms/security-scanner/IngestOngoingJobSync";
import { SecurityOngoingJobSync } from "@/components/organisms/security-scanner/SecurityOngoingJobSync";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { useShallow } from "zustand/react/shallow";
import { setDefaultOrgId } from "@/utils/defaultOrg";
import { cn } from "@/lib/utils";
import { filterNavItemsByCapabilities } from "@/lib/studio/navAccess";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  Skeleton,
} from "@ncoder/ui";

type StudioNavItem = {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  count?: number | string | null;
  moduleKey?: string;
};

/**
 * The migrations screens — `/migration-engine`, `/migration-portfolio`, and the
 * `/migration-engine/:planId` detail reached from the engine list — all call the
 * `playground` proxy slug, which ncoder-backend's `ServiceProxy.Registry` gates
 * behind `module:migrations`. Same key on both sides, or the sidebar advertises a
 * screen whose every request the proxy refuses.
 *
 * THIS HIDES A LINK; IT IS NOT THE ACCESS CONTROL. Typing the URL still renders
 * the page, because nothing in this app decides who may read the org's data — the
 * backend proxy does, returning 403 for a role without the module (proven in
 * ncoder-backend's `proxy_controller_test.exs`). The purpose here is that the nav
 * stops offering a screen that cannot load.
 *
 * Module scope and exported so the gate can be asserted against this definition
 * rather than against a copy of it in a test file: a test that declares its own
 * `{ moduleKey: "module:migrations" }` passes even after someone deletes the key
 * here, which is precisely the regression worth catching.
 */
export const MIGRATIONS_MODULE_KEY = "module:migrations";

export const MIGRATIONS_NAV_ITEMS: StudioNavItem[] = [
  {
    label: "Migration Engine",
    href: "/migration-engine",
    icon: GitCompareArrows,
    moduleKey: MIGRATIONS_MODULE_KEY,
  },
  {
    label: "Portfolio Pass",
    href: "/migration-portfolio",
    icon: Layers,
    moduleKey: MIGRATIONS_MODULE_KEY,
  },
];

function countLabel(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
}

function routeCrumbs(pathname: string): [string, string] {
  if (pathname.startsWith("/app/dashboard") || pathname.startsWith("/home")) return ["Workspace", "Home"];
  if (pathname.startsWith("/metadata")) return ["Workspace", "Metadata Explorer"];
  if (pathname.startsWith("/deployment")) return ["Delivery", "Deployment"];
  if (pathname.startsWith("/version-control") || pathname.startsWith("/git")) return ["Delivery", "Git & pipelines"];
  if (pathname.startsWith("/soql")) return ["Workspace", "SOQL tools"];
  if (pathname.startsWith("/profile")) return ["Account", "Profile"];
  if (pathname.startsWith("/ai") || pathname.startsWith("/modernise")) return ["Workspace", "AI tools"];
  if (pathname.startsWith("/support") || pathname.startsWith("/team") || pathname.startsWith("/help") || pathname.startsWith("/upgrade") || pathname.startsWith("/about")) {
    return ["Workspace", "Support"];
  }
  if (pathname.startsWith("/release-notes")) return ["Workspace", "Release Notes"];
  if (pathname.startsWith("/user-guide")) return ["Workspace", "User Guide"];
  if (pathname.startsWith("/text-to-soql")) return ["Workspace", "Text to SOQL"];
  if (pathname.startsWith("/security")) return ["Workspace", "Security Scanner"];
  if (pathname.startsWith("/org/connection")) return ["Org", "Connection Manager"];
  if (pathname.startsWith("/docs") || pathname.startsWith("/documentation")) return ["Operations", "Documentation"];
  // if (pathname.startsWith("/dev-agent")) return ["Workspace", "Dev Agent"];
  if (pathname.startsWith("/weave/runs")) return ["AI with Weave", "History"];
  if (pathname.startsWith("/weave")) return ["Delivery", "AI with Weave"];
  if (pathname.startsWith("/studio/metadata")) return ["Delivery", "Metadata explorer"];
  if (pathname.startsWith("/studio/deployment")) return ["Delivery", "Deployment"];
  if (pathname.startsWith("/studio/git")) return ["Delivery", "Git & pipelines"];
  if (pathname.startsWith("/studio/security")) return ["Operations", "Security Scanner"];
  if (pathname.startsWith("/studio/docs")) return ["Operations", "Documentation"];
  if (pathname.startsWith("/studio/org-admin")) return ["Operations", "Admin panel"];
  if (pathname.startsWith("/studio/connection")) return ["Operations", "Connection Manager"];
  if (pathname.startsWith("/deploy")) return ["Library", "Deploy"];
  if (pathname.startsWith("/tools")) return ["Delivery", "MCP"];
  if (pathname.startsWith("/agents/new")) return ["Agents", "Create agent"];
  if (pathname.startsWith("/agents/") && pathname.endsWith("/run")) return ["Agents", "Run agent"];
  if (pathname.startsWith("/agents/")) return ["Agents", "Agent workspace"];
  if (pathname.startsWith("/agents")) return ["Agents", "Agents"];
  if (pathname.startsWith("/scheduling")) return ["Agents", "Scheduling"];
  if (pathname.startsWith("/salesforce-releases") || pathname.startsWith("/release-scanner")) {
    return ["Delivery", "Salesforce releases"];
  }
  if (pathname.startsWith("/org360") || pathname.startsWith("/explorer")) return ["Delivery", "Org 360"];
  if (pathname.startsWith("/testing")) return ["Delivery", "Testing"];
  if (pathname.startsWith("/observability")) return ["Delivery", "Observability"];
  if (pathname.startsWith("/debugging")) return ["Delivery", "Salesforce Debugger"];
  if (pathname.startsWith("/reports")) return ["Delivery", "Reports"];
  if (pathname.startsWith("/runs")) return ["Delivery", "Reports"];
  if ((pathname.startsWith("/skills/") || pathname.startsWith("/migrations/")) && pathname.endsWith("/run")) return ["Library", "Run migration"];
  if (pathname.startsWith("/skills/") || pathname.startsWith("/migrations/")) {
    return ["Library", "Migration workspace"];
  }
  return ["Library", "Migrations"];
}

function isNavActive(item: StudioNavItem, pathname: string): boolean {
  return (
    (item.href === "/migrations" &&
      (pathname === "/migrations" ||
        pathname.startsWith("/migrations/") ||
        pathname.startsWith("/skills/"))) ||
    (item.href === "/agents" &&
      (pathname === "/agents" ||
        (pathname.startsWith("/agents/") &&
          !pathname.startsWith("/agents/new")))) ||
    (item.href === "/agents/new" && pathname.startsWith("/agents/new")) ||
    (item.href === "/tools" && pathname.startsWith("/tools")) ||
    (item.href === "/reports" &&
      (pathname === "/reports" ||
        pathname === "/runs" ||
        pathname.startsWith("/runs/") ||
        pathname.startsWith("/agent-runs/"))) ||
    (item.href === "/salesforce-releases" &&
      (pathname.startsWith("/salesforce-releases") || pathname.startsWith("/release-scanner"))) ||
    (item.href === "/org360" && (pathname.startsWith("/org360") || pathname.startsWith("/explorer"))) ||
    (item.href === "/studio/git" && (pathname.startsWith("/studio/git") || pathname.startsWith("/git") || pathname.startsWith("/version-control"))) ||
    (item.href !== "/migrations" &&
      item.href !== "/agents" &&
      item.href !== "/agents/new" &&
      item.href !== "/tools" &&
      item.href !== "/reports" &&
      item.href !== "/salesforce-releases" &&
      item.href !== "/org360" &&
      item.href !== "/studio/git" &&
      pathname.startsWith(item.href))
  );
}

function StudioNavItem({ item }: { item: StudioNavItem }) {
  const location = useLocation();
  const active = isNavActive(item, location.pathname);
  const count = countLabel(item.count);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
        <Link to={item.href}>
          <item.icon className="h-4 w-4 shrink-0" />
          <span>{item.label}</span>
        </Link>
      </SidebarMenuButton>
      {count && <SidebarMenuBadge>{count}</SidebarMenuBadge>}
    </SidebarMenuItem>
  );
}

function StudioNavGroup({ label, items }: { label: string; items: StudioNavItem[] }) {
  if (!items.length) return null;
  return (
    <SidebarGroup>
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => (
          <StudioNavItem key={item.href} item={item} />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

// Shown in place of a StudioNavGroup while the initial GetUserData fetch is
// still in flight, since nav visibility depends on the fetched role/capabilities.
function NavSkeletonGroup({ label, count }: { label: string; count: number }) {
  return (
    <SidebarGroup>
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <SidebarMenu>
        {Array.from({ length: count }, (_, i) => (
          <SidebarMenuItem key={i}>
            <div className="flex items-center gap-2 px-2 py-1.5">
              <Skeleton className="h-4 w-4 shrink-0 rounded" />
              <Skeleton className="h-3 w-24 rounded" />
            </div>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

export function StudioLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const orgMenuRef = useRef<HTMLDivElement | null>(null);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const { organizations, selectedOrg, selectOrganization, isLoadingUser } = useAuthStoreV1(
    useShallow((s) => ({
      organizations: s.organizations,
      selectedOrg: s.selectedOrg,
      selectOrganization: s.selectOrganization,
      isLoadingUser: s.isLoadingUser,
    }))
  );
  const [section, page] = routeCrumbs(location.pathname);
  const isMultiAgentRoute = location.pathname.startsWith("/weave");

  const [feedbackOpen, setFeedbackOpen]             = useState(false);
  const [requestFeatureOpen, setRequestFeatureOpen] = useState(false);
  const fullBleed =
    location.pathname.startsWith("/dev-agent") ||
    isMultiAgentRoute ||
    location.pathname.startsWith("/explorer")

  const capabilityKeys = selectedOrg?.myRole?.capabilityKeys ?? [];
  const isOwner = selectedOrg?.role === "OWNER";
  // Nav visibility depends on capabilityKeys/role from GetUserData — show
  // skeleton rows instead of a (wrongly) fully-open or fully-closed nav
  // while that initial fetch is still in flight.
  const isNavLoading = isLoadingUser && organizations.length === 0;

  const workspaceItems: StudioNavItem[] = filterNavItemsByCapabilities(
    [
      { label: "Home", href: "/app/dashboard", icon: Home },
      { label: "Metadata Explorer", href: "/metadata", icon: Database, moduleKey: "module:metadata-explorer" },
      { label: "Documentation", href: "/docs", icon: FileText, moduleKey: "module:documentation" },
      { label: "Text to SOQL", href: "/text-to-soql", icon: MessageSquareCode, moduleKey: "module:text-to-soql" },
      { label: "Security Scanner", href: "/security", icon: Shield, moduleKey: "module:security-scanner" },
      { label: "Connection Manager", href: "/org/connection", icon: HelpCircle },
    ],
    capabilityKeys
  );

  const agentItems: StudioNavItem[] = filterNavItemsByCapabilities(
    [
      { label: "Agents", href: "/agents", icon: Users, moduleKey: "module:agents" },
      { label: "Weave", href: "/weave", icon: WeaveIcon, moduleKey: "module:weave" },
      { label: "Migrations", href: "/migrations", icon: Grid2X2 },
      { label: "Scheduling", href: "/scheduling", icon: Calendar, moduleKey: "module:scheduling" },
    ],
    capabilityKeys
  );

  const deliveryItems: StudioNavItem[] = filterNavItemsByCapabilities(
    [
      { label: "Reports", href: "/reports", icon: FileText, moduleKey: "module:reports" },
      // { label: "Multi-Agent Runs", href: "/multi-agent/runs", icon: FileText },
      { label: "MCP", href: "/tools", icon: Code2, moduleKey: "module:mcp" },
      { label: "Git & pipelines", href: "/studio/git", icon: GitBranch, moduleKey: "module:git-pipelines" },
      { label: "Org 360", href: "/org360", icon: Network, moduleKey: "module:org-360" },
      { label: "Salesforce releases", href: "/salesforce-releases", icon: ShieldCheck, moduleKey: "module:salesforce-releases" },
      { label: "Deployment", href: "/deployment", icon: Layers },
      { label: "Testing", href: "/testing", icon: ListChecks, moduleKey: "module:testing" },
      { label: "Observability", href: "/observability", icon: BarChart3 },
      { label: "Salesforce Debugger", href: "/debugging", icon: Bug, moduleKey: "module:salesforce-debugger" },
    ],
    capabilityKeys
  );

  const operationItems: StudioNavItem[] = isOwner
    ? [{ label: "Admin panel", href: "/studio/org-admin", icon: UserCog }]
    : [];

  const orgLabel =
    selectedOrg?.orgName ||
    selectedOrg?.salesforceOrgId ||
    selectedOrg?.id ||
    "No org selected";
  const orgSubLabel = selectedOrg?.orgType
    ? `${selectedOrg.orgType} · ${selectedOrg.id?.slice(0, 8) ?? ""}`
    : selectedOrg?.id?.slice(0, 8) ?? "select an org";

  useEffect(() => {
    if (!orgMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!orgMenuRef.current?.contains(event.target as Node)) {
        setOrgMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOrgMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [orgMenuOpen]);

  const switchOrg = (orgId: string) => {
    if (!orgId || orgId === selectedOrg?.id) {
      setOrgMenuOpen(false);
      return;
    }
    // Selecting the organization IS the switch: every org-scoped store tears
    // itself down through `@/lib/org-scope`, and the previous org's cached
    // queries are evicted by the same fan-out.
    //
    // This used to also call `resetMultiAgentForTenantChange()` (now registered
    // centrally, so it runs however the org changes) and a bare
    // `queryClient.invalidateQueries()` — an unfiltered invalidation that
    // refetched every mounted query in the app, including the ones that are not
    // organization-scoped at all.
    selectOrganization(orgId);
    setDefaultOrgId(orgId);
    setOrgMenuOpen(false);
  };

  return (
    <div className="studio-shell rs-theme">
      <SidebarProvider defaultOpen={!isMultiAgentRoute}>
        <SecurityOngoingJobSync />
        <IngestOngoingJobSync />

        <Sidebar collapsible="icon">
          <SidebarHeader className="gap-2 p-2">
            <Link to="/migrations" className="studio-brand">
              <span className="studio-brand-mark">n</span>
              <span>ncoder.ai</span>
            </Link>

            <div ref={orgMenuRef} className="studio-org-select">
              <button
                type="button"
                className="studio-org-card"
                onClick={() => setOrgMenuOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={orgMenuOpen}
              >
                <div className="h-6 w-6 rounded-md bg-primary text-primary-foreground grid place-items-center text-[10px] font-bold shrink-0">
                  {orgLabel.slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <div className="truncate text-sm font-semibold">{orgLabel}</div>
                  <div className="truncate text-[11px] text-muted-foreground">{orgSubLabel}</div>
                </div>
                <ChevronDown className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform shrink-0", orgMenuOpen && "rotate-180")} />
              </button>

              {orgMenuOpen ? (
                <div className="studio-org-menu" role="listbox">
                  <div className="studio-org-menu-label">Switch org</div>
                  {organizations.length ? (
                    organizations.map((org) => {
                      const label = org.orgName || org.salesforceOrgId || org.id;
                      const active = org.id === selectedOrg?.id;
                      return (
                        <button
                          key={org.id}
                          type="button"
                          role="option"
                          aria-selected={active}
                          className={cn("studio-org-menu-item", active && "studio-org-menu-item-active")}
                          onClick={() => switchOrg(org.id)}
                        >
                          <span className="studio-org-menu-mark">{label.slice(0, 2).toUpperCase()}</span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold">{label}</span>
                            <span className="block truncate text-[11px] text-muted-foreground">
                              {org.orgType || "org"} · {org.id.slice(0, 8)}
                            </span>
                          </span>
                        </button>
                      );
                    })
                  ) : (
                    <div className="px-3 py-2 text-xs text-muted-foreground">No organizations available</div>
                  )}
                </div>
              ) : null}
            </div>
          </SidebarHeader>

          <SidebarContent className="studio-nav">
            {isNavLoading ? (
              <>
                <NavSkeletonGroup label="Workspace" count={6} />
                <NavSkeletonGroup label="Agents" count={4} />
                <NavSkeletonGroup label="Delivery" count={9} />
              </>
            ) : (
              <>
                <StudioNavGroup label="Workspace" items={workspaceItems} />
                <StudioNavGroup label="Agents" items={agentItems} />
                <StudioNavGroup label="Delivery" items={deliveryItems} />
                <StudioNavGroup label="Operations" items={operationItems} />
              </>
            )}

            <SidebarGroup>
              <SidebarGroupLabel>Support</SidebarGroupLabel>
              <SidebarMenu>
                {[
                  { label: "Share feedback", icon: MessageSquare, onClick: () => setFeedbackOpen(true) },
                  { label: "Request a feature", icon: Lightbulb, onClick: () => setRequestFeatureOpen(true) },
                  { label: "Release Notes", icon: Megaphone, href: "/release-notes" },
                  { label: "User Guide", icon: BookOpen, href: "/user-guide" },
                ].map(({ label, icon: Icon, onClick, href }) => (
                  <SidebarMenuItem key={label}>
                    {href ? (
                      <SidebarMenuButton asChild isActive={location.pathname === href} tooltip={label}>
                        <Link to={href}>
                          <Icon className="h-4 w-4 shrink-0" />
                          <span>{label}</span>
                        </Link>
                      </SidebarMenuButton>
                    ) : (
                      <SidebarMenuButton tooltip={label} onClick={onClick}>
                        <Icon className="h-4 w-4 shrink-0" />
                        <span>{label}</span>
                      </SidebarMenuButton>
                    )}
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroup>
          </SidebarContent>

          <FeedbackModal       open={feedbackOpen}       onClose={() => setFeedbackOpen(false)} />
          <RequestFeatureModal open={requestFeatureOpen} onClose={() => setRequestFeatureOpen(false)} />

          <SidebarRail />
        </Sidebar>

        <section className="studio-main">
          {/* Multi-agent hosts its OWN header (with the collapse toggle + chrome
              relocated into it), so the studio topbar is hidden there — removing
              the wrong "Library / Playbooks" crumb and reclaiming the height. */}
          {!isMultiAgentRoute && (
            <header className="studio-topbar studio-topbar--no-search">
              <div className="studio-crumbs min-w-0">
                <SidebarTrigger className="-ml-1 mr-2" />
                <span className="font-semibold text-foreground truncate">{section}</span>
                <span className="text-muted-foreground shrink-0">/</span>
                <span className="text-muted-foreground truncate">{page}</span>
              </div>

              <div className="studio-actions">
                <ActivityDropdown />
                <ThemeToggle />
                <button type="button" className="studio-icon-button" title="Notifications">
                  <Bell className="h-4 w-4" />
                </button>
                <UserProfile variant="topnav" />
              </div>
            </header>
          )}

          <main className={cn("studio-content", fullBleed && "studio-content-full")}>{children}</main>
        </section>
      </SidebarProvider>
    </div>
  );
}
